import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useDrafts } from "../../contexts/DraftsContext";
import {
  Search,
  ShoppingBag,
  Plus,
  Minus,
  Trash2,
  X,
  Package,
  Loader2,
  User,
  Phone,
  Receipt,
  Printer,
  Check,
  Store,
  ChevronDown,
  TrendingUp,
  DollarSign,
  Box,
  Truck,
} from "lucide-react";
import InvoiceModal from "../InvoiceModal";
import TierToggle, { priceForTier } from "../TierToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Press Enter inside any editable input → blur it (which commits the value).
// Press Esc → cancel and blur. Use as: onKeyDown={commitOnEnter}
const commitOnEnter = (e) => {
  if (e.key === "Enter" || e.key === "Escape") {
    e.preventDefault();
    e.currentTarget.blur();
  }
};

// =============== Customer Picker — choose existing or walk-in ===============
// CustomerPicker — STRICT mode (Feb 2026). Direct sales no longer accept
// inline walk-in names. The admin MUST select an existing customer or
// click "إضافة زبون جديد" which posts to /admin/customers and immediately
// selects the new customer. All invoices = debt. No "نقد" option anywhere.
const CustomerPicker = ({ customers, value, onChange, onCustomerAdded }) => {
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newMarket, setNewMarket] = useState("");
  const [adding, setAdding] = useState(false);
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    const all = customers || [];
    if (!t) return all.slice(0, 30);
    return all
      .filter((c) =>
        (c.full_name || "").toLowerCase().includes(t) ||
        (c.market_name || "").toLowerCase().includes(t) ||
        (c.phone || "").includes(t)
      )
      .slice(0, 60);
  }, [customers, q]);

  const submitNew = async () => {
    if (!newName.trim() || !newPhone.trim()) { toast.error("الاسم والهاتف مطلوبان"); return; }
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/admin/customers`, {
        full_name: newName.trim(),
        phone: newPhone.trim(),
        market_name: newMarket.trim(),
      });
      if (data.duplicate) toast.success("هذا الرقم مسجّل مسبقاً — تم اختيار الحساب القائم");
      else toast.success("تمّت إضافة الزبون");
      const c = data.customer;
      onCustomerAdded?.(c);
      onChange({
        customer_id: c.id,
        customer_name: c.full_name || c.customer_name || "",
        customer_phone: c.phone || "",
        market_name: c.market_name || "",
        customer_debt: c.customer_debt || 0,
      });
      setShowAdd(false);
      setNewName(""); setNewPhone(""); setNewMarket("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر إضافة الزبون");
    } finally { setAdding(false); }
  };

  const selected = value?.customer_id ? customers.find((c) => c.id === value.customer_id) : null;

  return (
    <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]" data-testid="ds-customer-block">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <label className="text-xs font-bold text-[#1E3A5F] flex items-center gap-1">
          <User className="w-3 h-3" /> الزبون <span className="text-[#E85D45]">*</span>
          <span className="text-[10px] font-normal text-[#5C6E64]">— يجب اختياره من المسجّلين</span>
        </label>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          data-testid="ds-add-customer-btn"
          className="bg-[#15803d] hover:bg-[#0f6630] text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> إضافة زبون جديد
        </button>
      </div>

      {/* Selected customer banner */}
      {selected ? (
        <div className="bg-[#ECFDF5] border-2 border-[#15803d]/40 rounded-xl p-2.5 flex items-center justify-between mb-2">
          <div className="min-w-0">
            <p className="font-black text-sm text-[#15803d] truncate">{selected.full_name || selected.customer_name}</p>
            <p className="text-[11px] text-[#5C6E64] truncate" dir="ltr">
              {selected.phone}{selected.market_name ? ` · ${selected.market_name}` : ""}
            </p>
            {Number(selected.customer_debt || 0) > 0 && (
              <p className="text-[11px] text-[#DC2626] font-bold mt-0.5" dir="ltr">
                دين سابق: {fmt(selected.customer_debt)} د.ع
              </p>
            )}
          </div>
          <button
            onClick={() => onChange({ customer_id: "", customer_name: "", customer_phone: "", market_name: "" })}
            data-testid="ds-clear-customer"
            className="text-[10px] text-[#5C6E64] hover:text-[#DC2626] flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> تغيير
          </button>
        </div>
      ) : (
        <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-xl p-2 text-[11px] text-[#92400E] mb-2 leading-relaxed">
          اختر زبوناً من القائمة أدناه — لو ما موجود اضغط <b>«إضافة زبون جديد»</b> أولاً ثم اختره. <b>كل الفواتير دين</b> ولا يُخصم إلا بسند قبض.
        </div>
      )}

      {/* Search */}
      <div className="relative mb-2">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6E64]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="ds-customer-search"
          placeholder="ابحث بالاسم/الماركت/الهاتف..."
          className="w-full pr-8 pl-3 py-1.5 rounded-full border border-[#3B82F6] text-xs"
        />
      </div>

      <div className="max-h-56 overflow-y-auto space-y-1 border border-[#E4EBE6] rounded-xl p-1.5 bg-[#F8FAFC]">
        {list.length === 0 ? (
          <p className="text-center text-xs text-[#5C6E64] py-3">لا توجد نتائج</p>
        ) : list.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange({
              customer_id: c.id,
              customer_name: c.full_name || c.customer_name || "",
              customer_phone: c.phone || "",
              market_name: c.market_name || "",
              customer_debt: c.customer_debt || 0,
            })}
            data-testid={`ds-pick-customer-${c.id}`}
            className={`w-full text-right rounded-lg px-2 py-1.5 text-xs flex items-center justify-between border ${
              value?.customer_id === c.id ? "bg-[#15803d]/10 border-[#15803d]" : "bg-white border-[#E4EBE6] hover:bg-[#F0F9FF]"
            }`}
          >
            <div className="min-w-0">
              <p className="font-bold truncate text-[#1E3A5F]">{c.full_name || c.customer_name}</p>
              <p className="text-[10px] text-[#5C6E64] truncate" dir="ltr">{c.phone} {c.market_name ? `· ${c.market_name}` : ""}</p>
            </div>
            {Number(c.customer_debt || 0) > 0 && (
              <span className="text-[10px] bg-[#E85D45]/15 text-[#E85D45] px-1.5 py-0.5 rounded-full" dir="ltr">{fmt(c.customer_debt)}</span>
            )}
          </button>
        ))}
      </div>

      {/* Add-customer modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={() => !adding && setShowAdd(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-4 space-y-3 shadow-2xl" dir="rtl" data-testid="ds-add-customer-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-[#1E3A5F]">إضافة زبون جديد</h3>
              <button onClick={() => setShowAdd(false)} disabled={adding}><X className="w-4 h-4 text-[#5C6E64]" /></button>
            </div>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="اسم الزبون"
              data-testid="ds-new-customer-name"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] text-sm"
            />
            <input
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="07XXXXXXXXX"
              data-testid="ds-new-customer-phone"
              dir="ltr"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] text-sm"
            />
            <input
              value={newMarket}
              onChange={(e) => setNewMarket(e.target.value)}
              placeholder="اسم الماركت (اختياري)"
              data-testid="ds-new-customer-market"
              className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] text-sm"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowAdd(false)} disabled={adding} className="flex-1 bg-[#F3F6F4] text-[#5C6E64] font-bold py-2 rounded-full">إلغاء</button>
              <button
                onClick={submitNew}
                disabled={adding}
                data-testid="ds-new-customer-save"
                className="flex-[2] bg-[#15803d] hover:bg-[#0f6630] disabled:opacity-60 text-white font-bold py-2 rounded-full flex items-center justify-center gap-1"
              >
                {adding ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> جاري الحفظ...</> : "حفظ الزبون"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============== Product Catalog Grid ===============
const ProductCatalog = ({ products, onAdd, cart, tier }) => {
  const [q, setQ] = useState("");
  const inCart = useMemo(() => {
    const m = {};
    cart.forEach((c) => { m[c.product_id] = c.quantity; });
    return m;
  }, [cart]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return products;
    return products.filter((p) => (p.name_ar || "").toLowerCase().includes(t));
  }, [products, q]);

  return (
    <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]" data-testid="ds-catalog">
      <div className="flex justify-between items-center mb-2">
        <h4 className="font-bold text-[#1E3A5F] flex items-center gap-1"><Package className="w-4 h-4" /> المنتجات <span className="text-xs text-[#5C6E64]">({filtered.length})</span></h4>
        <div className="relative w-1/2">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6E64]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="ds-product-search"
            placeholder="ابحث..."
            className="w-full pr-8 pl-3 py-1.5 rounded-full border border-[#3B82F6] text-xs"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[55vh] overflow-y-auto">
        {filtered.map((p) => {
          const qty = inCart[p.id] || 0;
          const outOfStock = (p.stock || 0) <= 0;
          const effectivePrice = priceForTier(p, tier);
          return (
            <button
              key={p.id}
              onClick={() => !outOfStock && onAdd(p)}
              disabled={outOfStock}
              data-testid={`ds-product-${p.id}`}
              className={`relative bg-[#F8FAFC] hover:bg-[#F0F9FF] border-2 rounded-xl p-2 text-right transition-all ${
                qty > 0 ? "border-[#7ED321] ring-2 ring-[#7ED321]/30" : "border-[#E4EBE6]"
              } ${outOfStock ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {qty > 0 && (
                <span className="absolute top-1 right-1 bg-[#7ED321] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-black z-10" dir="ltr">
                  {qty}
                </span>
              )}
              {p.image_path ? (
                <img src={`${API}/files/${p.image_path}`} alt="" className="w-full h-20 object-cover rounded-lg mb-1" />
              ) : <div className="w-full h-20 bg-white rounded-lg flex items-center justify-center mb-1"><Package className="w-6 h-6 text-[#5C6E64]" /></div>}
              <p className="text-[11px] font-bold text-[#111814] line-clamp-2 min-h-[28px]">{p.name_ar}</p>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-[#15803d] font-bold" dir="ltr">{fmt(effectivePrice)} د.ع</span>
                <span className="text-[9px] text-[#5C6E64]">المخزن: <span dir="ltr">{p.stock}</span></span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =============== Receipt Modal (preview / print before saving) ===============
const ReceiptPreview = ({ payload, onClose, onConfirm, saving, mujahezList, assignedMujahezId, onAssignChange }) => {
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-2" onClick={onClose} data-testid="ds-receipt-preview" dir="rtl">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 sticky top-0 flex justify-between items-start">
          <div>
            <p className="text-[10px] opacity-80">معاينة فاتورة البيع المباشر</p>
            <h3 className="font-bold text-lg">{payload.customer_name}</h3>
            <p className="text-[10px] opacity-90" dir="ltr">{payload.customer_phone}</p>
          </div>
          <button onClick={onClose} data-testid="ds-receipt-close" className="bg-white/20 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3 space-y-2">
          {payload.items.map((it, i) => (
            <div key={i} className="bg-[#F8FAFC] rounded-xl p-2 flex justify-between text-xs">
              <p className="font-bold flex-1 truncate">{it.name_ar}</p>
              <p className="text-[#5C6E64] mx-2" dir="ltr">{it.quantity} × {fmt(it.price)}</p>
              <p className="font-black text-[#1E3A5F]" dir="ltr">{fmt(it.price * it.quantity)}</p>
            </div>
          ))}
          <div className="bg-[#FEE2E2] border border-[#DC2626] rounded-2xl p-3 space-y-1">
            <div className="flex justify-between font-black text-[#7F1D1D]"><span>الإجمالي (دين كامل)</span><span dir="ltr">{fmt(payload.total)} د.ع</span></div>
            <p className="text-[10px] text-[#7F1D1D]">هذه الفاتورة دين بالكامل — لا تُخصم إلا بسند قبض لاحقاً.</p>
          </div>

          {/* Mujahez assignment — pre-confirmation. When chosen, the invoice
              is routed to the selected mujahez's app exactly like a regular
              customer order (status starts as "confirmed" pending preparation). */}
          <div className="bg-[#F0FDF4] rounded-2xl p-3 border-2 border-[#7ED321]/40 space-y-1.5">
            <label className="text-xs font-bold text-[#1E3A5F] flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-[#15803d]" />
              تعيين المجهّز <span className="text-[10px] font-normal text-[#5C6E64]">(اختياري)</span>
            </label>
            <select
              value={assignedMujahezId}
              onChange={(e) => onAssignChange(e.target.value)}
              data-testid="ds-receipt-mujahez-select"
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#7ED321]/40 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#7ED321]"
            >
              <option value="">— بدون مجهّز (بيع نقدي مكتمل) —</option>
              {(mujahezList || []).map((m) => (
                <option key={m.id} value={m.id}>{m.full_name}{m.pending_count !== undefined ? ` (${m.pending_count} بانتظار)` : ""}</option>
              ))}
            </select>
            {assignedMujahezId && (
              <p className="text-[10px] text-[#15803d] flex items-center gap-1">
                <Check className="w-3 h-3" /> ستظهر الفاتورة في تطبيق المجهّز فوراً وهو يحدّد السائق.
              </p>
            )}
          </div>

          <button
            onClick={onConfirm}
            disabled={saving}
            data-testid="ds-receipt-confirm"
            className="w-full bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2.5 rounded-full flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? "جاري الحفظ..." : (assignedMujahezId ? "تأكيد وإرسال للمجهّز" : "تأكيد البيع وإصدار الفاتورة")}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============== Stats Strip (today / month / all) ===============
const DirectSaleStats = ({ refreshKey }) => {
  const [range, setRange] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/direct-sale/stats`, { params: { range } });
        if (!cancelled) setData(data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [range, refreshKey]);

  const ranges = [
    { id: "today", label: "اليوم" },
    { id: "month", label: "الشهر" },
    { id: "all", label: "الكل" },
  ];
  return (
    <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]" data-testid="ds-stats">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h4 className="font-bold text-[#1E3A5F] flex items-center gap-1">
          <TrendingUp className="w-4 h-4" /> إحصائيات البيع المباشر
        </h4>
        <div className="flex gap-1 bg-[#F3F6F4] rounded-full p-0.5">
          {ranges.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRange(r.id)}
              data-testid={`ds-stats-range-${r.id}`}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                range === r.id ? "bg-[#1E3A5F] text-white" : "text-[#5C6E64] hover:bg-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {loading || !data ? (
        <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#1E3A5F]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <StatCard icon={Receipt} label="عدد الفواتير" value={fmt(data.invoices_count)} color="#3B82F6" />
            <StatCard icon={DollarSign} label="المبلغ المباع" value={fmt(data.total_revenue)} unit="د.ع" color="#15803d" />
            <StatCard icon={TrendingUp} label="صافي الربح" value={fmt(data.profit)} unit="د.ع" color={data.profit >= 0 ? "#7C3AED" : "#E85D45"} />
            <StatCard icon={Box} label="إجمالي الكراتين" value={fmt(data.total_cartons)} color="#F59E0B" />
            <StatCard icon={Package} label="أصناف مختلفة" value={fmt(data.unique_items_count)} color="#0EA5E9" />
            <StatCard icon={DollarSign} label="المتبقّي (دين)" value={fmt(data.unpaid)} unit="د.ع" color="#E85D45" />
          </div>
          {data.top_items?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#E4EBE6]">
              <p className="text-[10px] font-bold text-[#5C6E64] mb-1">أكثر المواد مبيعاً ({data.range === "today" ? "اليوم" : data.range === "month" ? "الشهر" : "الكل"}):</p>
              <div className="flex flex-wrap gap-1.5">
                {data.top_items.map((it, i) => (
                  <span key={i} className="bg-[#F0F9FF] text-[#1E3A5F] text-[10px] font-bold rounded-full px-2 py-0.5 border border-[#3B82F6]/20">
                    {it.name} · <span dir="ltr" className="text-[#7C3AED]">{fmt(it.qty)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, unit, color }) => (
  <div className="bg-[#F8FAFC] rounded-xl p-2 border border-[#E4EBE6]" data-testid={`ds-stat-${label}`}>
    <div className="flex items-center gap-1 mb-0.5">
      <Icon className="w-3 h-3" style={{ color }} />
      <p className="text-[9px] text-[#5C6E64] truncate">{label}</p>
    </div>
    <p className="font-black text-sm leading-tight" style={{ color }} dir="ltr">
      {value}{unit ? <span className="text-[9px] mr-0.5 opacity-70">{unit}</span> : null}
    </p>
  </div>
);

// =============== Post-Sale Assignment Modal ===============
// Appears immediately after the admin confirms a direct-sale invoice.
// Shows two dropdowns (mujahez / driver) — both optional — and a final
// "طباعة الفاتورة" button that closes this modal and opens InvoiceModal.
const PostSaleAssignment = ({ order, onPrint, onSkip }) => {
  const [mujahezList, setMujahezList] = useState([]);
  const [driverList, setDriverList] = useState([]);
  const [mujahezId, setMujahezId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("assign"); // assign | print

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mRes, dRes] = await Promise.all([
          axios.get(`${API}/admin/mujahez`),
          axios.get(`${API}/admin/drivers`),
        ]);
        if (cancelled) return;
        setMujahezList(mRes.data || []);
        setDriverList(dRes.data || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const assignMujahez = async () => {
    if (!mujahezId) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/orders/${order.id}/assign-mujahez`, { mujahez_id: mujahezId });
      toast.success("تمّ تعيين المجهّز");
      const picked = mujahezList.find((m) => m.id === mujahezId);
      if (picked) order.mujahez_name = picked.full_name;
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل تعيين المجهّز");
    } finally { setBusy(false); }
  };

  const assignDriver = async () => {
    if (!driverId) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/orders/${order.id}/assign`, { driver_id: driverId });
      toast.success("تمّ تعيين السائق");
      const picked = driverList.find((d) => d.id === driverId);
      if (picked) order.driver_name = picked.full_name;
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل تعيين السائق");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-2" data-testid="ds-post-sale-modal" dir="rtl">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-l from-[#15803d] to-[#7ED321] text-white p-4 sticky top-0">
          <div className="flex items-center gap-2 mb-1">
            <Check className="w-5 h-5" />
            <h3 className="font-bold text-lg">تمّ إنشاء الفاتورة #{order.invoice_number}</h3>
          </div>
          <p className="text-xs opacity-90">الزبون: <b>{order.user_name}</b></p>
        </div>

        {step === "assign" ? (
          <div className="p-4 space-y-4">
            {loading ? (
              <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#15803d]" /></div>
            ) : (
              <>
                {/* Mujahez */}
                <div className="bg-[#F0FDF4] rounded-2xl p-3 border-2 border-[#7ED321]/40">
                  <label className="text-sm font-bold text-[#1E3A5F] mb-2 flex items-center gap-1.5">
                    <Package className="w-4 h-4 text-[#15803d]" />
                    1️⃣ تعيين المجهّز <span className="text-[10px] font-normal text-[#5C6E64]">(اختياري)</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={mujahezId}
                      onChange={(e) => setMujahezId(e.target.value)}
                      data-testid="ds-post-mujahez-select"
                      className="flex-1 px-3 py-2 rounded-xl bg-white border border-[#7ED321]/40 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#7ED321]"
                    >
                      <option value="" disabled>اختر مجهّزاً...</option>
                      {mujahezList.map((m) => (
                        <option key={m.id} value={m.id}>{m.full_name}{m.pending_count !== undefined ? ` (${m.pending_count} بانتظار)` : ""}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={assignMujahez}
                      disabled={!mujahezId || busy}
                      data-testid="ds-post-mujahez-assign"
                      className="bg-[#15803d] hover:bg-[#0e6b30] disabled:opacity-40 text-white text-xs font-bold rounded-xl px-3"
                    >
                      تعيين
                    </button>
                  </div>
                  {order.mujahez_name && (
                    <p className="text-xs text-[#15803d] mt-1.5 flex items-center gap-1">
                      <Check className="w-3 h-3" /> تم: <b>{order.mujahez_name}</b>
                    </p>
                  )}
                </div>

                {/* Driver */}
                <div className="bg-[#EFF6FF] rounded-2xl p-3 border-2 border-[#3B82F6]/40">
                  <label className="text-sm font-bold text-[#1E3A5F] mb-2 flex items-center gap-1.5">
                    <Truck className="w-4 h-4 text-[#3B82F6]" />
                    2️⃣ تعيين السائق <span className="text-[10px] font-normal text-[#5C6E64]">(اختياري)</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={driverId}
                      onChange={(e) => setDriverId(e.target.value)}
                      data-testid="ds-post-driver-select"
                      className="flex-1 px-3 py-2 rounded-xl bg-white border border-[#3B82F6]/40 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                    >
                      <option value="" disabled>اختر سائقاً...</option>
                      {driverList.map((d) => (
                        <option key={d.id} value={d.id}>{d.full_name}{d.active_orders !== undefined ? ` (${d.active_orders} نشطة)` : ""}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={assignDriver}
                      disabled={!driverId || busy}
                      data-testid="ds-post-driver-assign"
                      className="bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-40 text-white text-xs font-bold rounded-xl px-3"
                    >
                      تعيين
                    </button>
                  </div>
                  {order.driver_name && (
                    <p className="text-xs text-[#3B82F6] mt-1.5 flex items-center gap-1">
                      <Check className="w-3 h-3" /> تم: <b>{order.driver_name}</b>
                    </p>
                  )}
                </div>

                {/* Footer actions */}
                <div className="pt-2 border-t border-[#E4EBE6] grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={onSkip}
                    data-testid="ds-post-skip-btn"
                    className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full text-sm"
                  >
                    تخطّي
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep("print")}
                    data-testid="ds-post-continue-btn"
                    className="bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold py-2.5 rounded-full text-sm flex items-center justify-center gap-1.5"
                  >
                    <Printer className="w-4 h-4" /> متابعة للطباعة
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="bg-[#F0FDF4] rounded-2xl p-4 text-center border-2 border-[#7ED321]/40">
              <Printer className="w-12 h-12 mx-auto text-[#15803d] mb-2" />
              <h4 className="font-black text-lg text-[#1E3A5F]">جاهزة للطباعة</h4>
              <p className="text-xs text-[#5C6E64] mt-1">انقر الزر أدناه لفتح الفاتورة في نافذة الطباعة.</p>
            </div>
            <div className="space-y-1.5 text-xs">
              {order.mujahez_name && <p className="bg-[#F0FDF4] rounded-lg p-2 flex justify-between"><span>📦 المجهّز:</span> <b className="text-[#15803d]">{order.mujahez_name}</b></p>}
              {order.driver_name && <p className="bg-[#EFF6FF] rounded-lg p-2 flex justify-between"><span>🚚 السائق:</span> <b className="text-[#3B82F6]">{order.driver_name}</b></p>}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button
                type="button"
                onClick={() => setStep("assign")}
                className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full text-sm"
              >
                رجوع
              </button>
              <button
                type="button"
                onClick={onPrint}
                data-testid="ds-post-print-btn"
                className="bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-2.5 rounded-full text-sm flex items-center justify-center gap-1.5"
              >
                <Printer className="w-4 h-4" /> طباعة الفاتورة
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============== Cart Row (with draft state for safe editing) ===============
// Keeps a LOCAL draft for the price + qty inputs so the user can fully
// erase the field (going blank, even "0") without the cart state
// snapping back. The cart only updates onBlur OR on Enter — and an empty
// value falls back to the previous valid number (no auto-zeroing, no row
// deletion).
const CartRow = ({ row, index, master, onSetPrice, onSetQty, onDec, onAdd, onRemove }) => {
  const cost = Number(master?.cost_price || 0);
  const stock = Number(master?.stock ?? row.stock ?? 0);
  const belowCost = cost > 0 && Number(row.price || 0) < cost;
  const [priceDraft, setPriceDraft] = useState(String(row.price ?? ""));
  const [qtyDraft, setQtyDraft] = useState(String(row.quantity ?? ""));
  // Sync drafts when the row updates from outside (e.g., qty increased via +)
  useEffect(() => { setPriceDraft(String(row.price ?? "")); }, [row.price]);
  useEffect(() => { setQtyDraft(String(row.quantity ?? "")); }, [row.quantity]);

  const commitPrice = () => {
    const t = priceDraft.trim();
    if (t === "") { setPriceDraft(String(row.price ?? "")); return; }
    const v = Number(t);
    if (!isFinite(v) || v < 0) { setPriceDraft(String(row.price ?? "")); return; }
    onSetPrice(index, v);
  };
  const commitQty = () => {
    const t = qtyDraft.trim();
    if (t === "") { setQtyDraft(String(row.quantity ?? "")); return; }
    const v = parseInt(t, 10);
    if (!isFinite(v) || v < 1) { setQtyDraft(String(row.quantity ?? "")); return; }
    onSetQty(index, v);
  };

  return (
    <div
      data-testid={`ds-cart-row-${index}`}
      className={`rounded-xl p-2 flex items-center gap-1 ${belowCost ? "bg-[#FEF2F2] border-2 border-[#E85D45]/40" : "bg-[#F8FAFC]"}`}
    >
      <div className="flex-1 min-w-0">
        <p
          className="text-xs font-bold truncate flex items-center gap-1 cursor-help"
          title={`💰 سعر الشراء: ${fmt(cost)} د.ع · 🏬 المتوفر: ${stock}`}
          data-testid={`ds-cart-name-${index}`}
        >
          {belowCost && <span className="text-[#E85D45]">⚠️</span>}
          {row.name_ar}
        </p>
        <input
          type="text"
          inputMode="decimal"
          lang="en"
          pattern="[0-9.]*"
          value={priceDraft}
          onChange={(e) => setPriceDraft(e.target.value.replace(/[^\d.]/g, ""))}
          onBlur={commitPrice}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commitPrice(); e.currentTarget.blur(); }
            if (e.key === "Escape") { setPriceDraft(String(row.price ?? "")); e.currentTarget.blur(); }
          }}
          data-testid={`ds-cart-price-${index}`}
          title="امسح واكتب أي رقم بحرية — Enter لتثبيت"
          className={`w-24 px-1.5 py-0.5 mt-0.5 text-[10px] font-bold rounded border ${belowCost ? "border-[#E85D45] text-[#E85D45] bg-white" : "border-[#3B82F6]/30 text-[#5C6E64] bg-white"}`}
          dir="ltr"
        />
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onDec(index)} className="w-6 h-6 bg-[#F3F6F4] hover:bg-[#E4EBE6] rounded-full flex items-center justify-center"><Minus className="w-3 h-3" /></button>
        <input
          type="text"
          inputMode="numeric"
          lang="en"
          pattern="[0-9]*"
          value={qtyDraft}
          onChange={(e) => setQtyDraft(e.target.value.replace(/[^\d]/g, ""))}
          onBlur={commitQty}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commitQty(); e.currentTarget.blur(); }
            if (e.key === "Escape") { setQtyDraft(String(row.quantity ?? "")); e.currentTarget.blur(); }
          }}
          data-testid={`ds-cart-qty-${index}`}
          className="w-10 text-center text-xs font-bold bg-white border border-[#3B82F6] rounded"
          dir="ltr"
        />
        <button onClick={() => onAdd(row)} className="w-6 h-6 bg-[#7ED321] hover:bg-[#6cb91d] text-white rounded-full flex items-center justify-center"><Plus className="w-3 h-3" /></button>
        <button onClick={() => onRemove(index)} className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1"><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
};

// =============== Main Direct Sale Tab ===============
const DirectSaleTab = () => {
  const { clearDirty, saveDraft, loadDraft } = useDrafts();
  const stash = useRef(loadDraft("direct-sale") || null).current;
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(stash?.cart || []); // {product_id,name_ar,price,quantity,stock}
  const [customer, setCustomer] = useState(stash?.customer || { customer_id: "", customer_name: "", customer_phone: "", market_name: "" });
  const [amountPaid, setAmountPaid] = useState(stash?.amountPaid || "");
  // Cash-vs-debt toggle (Feb 28, 2026): direct sales default to "عاجل"
  // (debt — adds to customer ledger). Admin can flip to "نقد" and the
  // invoice settles immediately — no debt, full amount credited to the
  // main cash box on the backend side.
  const [isCashSale, setIsCashSale] = useState(false);
  const [notes, setNotes] = useState(stash?.notes || "");
  const [showReceipt, setShowReceipt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOrder, setSavedOrder] = useState(null);
  const [tier, setTier] = useState(stash?.tier || "A_plus"); // A_plus (royal blue) | B_plus (royal purple)
  const [statsRefresh, setStatsRefresh] = useState(0); // bump after each sale to refetch stats
  const [postSaleOrder, setPostSaleOrder] = useState(null); // intermediate step: assign mujahez/driver before printing
  const [mujahezList, setMujahezList] = useState([]);
  const [assignedMujahezId, setAssignedMujahezId] = useState("");
  // Per user request: when admin sells direct to a registered customer
  // whose phone is broken, the sale should accrue the per-piece reward
  // AND tally toward the monthly markets contest. This toggle (default ON)
  // lets the admin opt-out for unusual cash-only walk-ins.
  const [accrueToCustomer, setAccrueToCustomer] = useState(true);

  // Persist field content + mark dirty
  useEffect(() => {
    const hasContent =
      cart.length > 0 ||
      customer.customer_name?.trim() ||
      customer.customer_phone?.trim() ||
      customer.market_name?.trim() ||
      amountPaid.trim() ||
      notes.trim();
    if (hasContent) {
      saveDraft("direct-sale", "بيع مباشر قيد الإدخال", { cart, customer, amountPaid, notes, tier });
    }
  }, [cart, customer, amountPaid, notes, tier, saveDraft]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pRes, cRes, mRes] = await Promise.all([
        axios.get(`${API}/admin/products`),
        axios.get(`${API}/admin/customers`),
        axios.get(`${API}/admin/mujahez`).catch(() => ({ data: [] })),
      ]);
      setProducts(pRes.data || []);
      setCustomers(cRes.data || []);
      setMujahezList(mRes.data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const addToCart = (p) => {
    setCart((prev) => {
      const idx = prev.findIndex((x) => x.product_id === p.id);
      if (idx >= 0) {
        const next = [...prev];
        if (next[idx].quantity + 1 > p.stock) { toast.error("لا يوجد كمية كافية في المخزن"); return prev; }
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      const tierPrice = priceForTier(p, tier);
      // `original_price` is the tier price at add-time. Any inline edit
      // that moves `price` away from this value marks the row as a
      // manual override (see setPrice below), which the backend uses to
      // distinguish "admin intentionally changed price" from "frontend
      // sent a stale cached price". Without this flag the server always
      // uses the current DB price for the row.
      return [...prev, { product_id: p.id, name_ar: p.name_ar, price: tierPrice, original_price: tierPrice, is_price_override: false, quantity: 1, stock: p.stock }];
    });
  };
  const decQty = (i) => setCart((prev) => prev.map((x, idx) => idx === i ? { ...x, quantity: Math.max(0, x.quantity - 1) } : x).filter((x) => x.quantity > 0));
  const removeItem = (i) => setCart((prev) => prev.filter((_, idx) => idx !== i));
  const setQty = (i, v) => {
    const q = Math.max(1, Number(v) || 1); // never drop below 1; row deletion is via the trash button
    setCart((prev) => prev.map((x, idx) => idx === i ? { ...x, quantity: q } : x));
  };

  // Inline price editor for cart rows — accepts ANY value silently. No
  // validation popups even when the new price is below cost. The card
  // still highlights below-cost rows in red but never blocks the change.
  const setPrice = (i, newVal) => {
    const newPrice = Number(newVal);
    if (isNaN(newPrice) || newPrice < 0) return;
    setCart((prev) => prev.map((x, idx) => {
      if (idx !== i) return x;
      const orig = Number(x.original_price ?? x.price ?? 0);
      // Consider it "override" only if the admin actually changed away
      // from the original tier price (rounded to 1 IQD to ignore noise).
      const overridden = Math.abs(newPrice - orig) > 0.5;
      return { ...x, price: newPrice, is_price_override: overridden };
    }));
  };

  const subtotal = useMemo(() => cart.reduce((s, c) => s + c.price * c.quantity, 0), [cart]);

  const openReceipt = () => {
    if (!customer.customer_id) { toast.error("اختر زبوناً مسجّلاً أولاً (أو اضغط «إضافة زبون جديد»)"); return; }
    if (cart.length === 0) { toast.error("أضف منتجاً واحداً على الأقل"); return; }
    setShowReceipt(true);
  };

  const confirmSale = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_id: customer.customer_id,
        customer_name: customer.customer_name.trim(),
        customer_phone: customer.customer_phone.trim(),
        market_name: customer.market_name?.trim() || "",
        items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity, price: c.price, is_price_override: !!c.is_price_override })),
        // Cash-vs-debt: when isCashSale=true, send amount_paid=total so the
        // backend marks the invoice fully settled and credits the cash box.
        amount_paid: isCashSale ? cart.reduce((s, c) => s + c.price * c.quantity, 0) : 0,
        payment_mode: isCashSale ? "cash" : "debt",
        accrue_to_customer_account: accrueToCustomer,
        notes: notes.trim(),
        assigned_mujahez_id: assignedMujahezId || undefined,
      };
      const { data } = await axios.post(`${API}/admin/direct-sale`, payload);
      toast.success(
        assignedMujahezId
          ? `تمّ إرسال الفاتورة #${data.invoice_number} للمجهّز`
          : `تمّ البيع · فاتورة #${data.invoice_number}`
      );
      // Build the order object used by InvoiceModal AND by PostSaleAssignment.
      // previous_customer_debt = the customer's live debt BEFORE this sale,
      // captured now so the printed invoice can show "الدين المتبقي السابق"
      // even though we reset the customer state a few lines below.
      const builtOrder = {
        id: data.id,
        invoice_number: data.invoice_number,
        user_name: data.customer_name,
        user_phone: data.customer_phone,
        items: data.items,
        total: data.total,
        amount_paid: data.amount_paid,
        amount_remaining: data.amount_remaining,
        address: "بيع مباشر",
        notes: notes,
        market_name: customer.market_name || "",
        created_at: data.created_at,
        status: data.status || "delivered",
        driver_name: "",
        mujahez_name: data.mujahez_name || "",
        previous_customer_debt: Number(customer.customer_debt || 0),
      };
      // If admin already chose a mujahez before confirming, skip the
      // post-sale assignment screen and go straight to print preview —
      // the mujahez will route from his own app.
      if (assignedMujahezId) {
        setSavedOrder(builtOrder);
      } else {
        setPostSaleOrder(builtOrder);
      }
      setShowReceipt(false);
      // reset
      setCart([]);
      setAmountPaid("");
      setNotes("");
      setCustomer({ customer_id: "", customer_name: "", customer_phone: "", market_name: "" });
      setAssignedMujahezId("");
      clearDirty("direct-sale");
      // refresh stock numbers + stats
      fetchData();
      setStatsRefresh((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل البيع");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="direct-sale-tab" dir="rtl">
      <div className="bg-gradient-to-l from-[#7ED321] to-[#15803d] text-white rounded-2xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag className="w-5 h-5" />
              <h3 className="font-bold text-lg">بيع مباشر</h3>
            </div>
            <p className="text-xs opacity-90">اختر مستوى الزبون (A+ / B+) — اختر زبوناً — أضف المنتجات — اضغط "تم الشراء" لإصدار الفاتورة.</p>
          </div>
          <div className="bg-white/15 backdrop-blur rounded-2xl p-2 flex items-center gap-2">
            <span className="text-[10px] font-bold opacity-90">مستوى الأسعار:</span>
            <TierToggle value={tier} onChange={setTier} size="md" />
          </div>
        </div>
      </div>

      <DirectSaleStats refreshKey={statsRefresh} />

      <div className="grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 space-y-3">
          <CustomerPicker
            customers={customers}
            value={customer}
            onChange={setCustomer}
            onCustomerAdded={(c) => setCustomers((prev) => prev.some((x) => x.id === c.id) ? prev : [c, ...prev])}
          />
          <ProductCatalog products={products} cart={cart} onAdd={addToCart} tier={tier} />
        </div>

        {/* Cart sidebar */}
        <div className="space-y-3">
          <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] sticky top-2">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-bold text-[#1E3A5F] flex items-center gap-1"><Receipt className="w-4 h-4" /> السلة <span className="text-xs text-[#5C6E64]">({cart.length})</span></h4>
              {cart.length > 0 && (
                <button onClick={() => setCart([])} data-testid="ds-cart-clear" className="text-[10px] text-[#E85D45] hover:underline">تفريغ</button>
              )}
            </div>
            {cart.length === 0 ? (
              <p className="text-center text-xs text-[#5C6E64] py-6">السلة فارغة. اضغط على منتج لإضافته.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1.5">
                {cart.map((c, i) => (
                  <CartRow
                    key={c.product_id}
                    row={c}
                    index={i}
                    master={products.find((p) => p.id === c.product_id)}
                    onSetPrice={setPrice}
                    onSetQty={setQty}
                    onDec={decQty}
                    onAdd={(row) => addToCart({ id: row.product_id, name_ar: row.name_ar, price: row.price, stock: row.stock })}
                    onRemove={removeItem}
                  />
                ))}
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-[#E4EBE6] space-y-2">
              <div className="flex justify-between"><span className="text-xs">الإجمالي</span><span className="font-black text-[#1E3A5F]" dir="ltr">{fmt(subtotal)} د.ع</span></div>
              {/* Cash-vs-debt toggle — default = debt (red urgent banner) */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setIsCashSale(false)}
                  data-testid="ds-mode-debt"
                  className={`text-xs font-extrabold py-2 rounded-xl border-2 transition-all ${!isCashSale ? "bg-[#DC2626] text-white border-[#DC2626] shadow" : "bg-white text-[#5B6B62] border-[#E4EBE6]"}`}
                >
                  🔴 عاجل (دين)
                </button>
                <button
                  type="button"
                  onClick={() => setIsCashSale(true)}
                  data-testid="ds-mode-cash"
                  className={`text-xs font-extrabold py-2 rounded-xl border-2 transition-all ${isCashSale ? "bg-[#15803D] text-white border-[#15803D] shadow" : "bg-white text-[#5B6B62] border-[#E4EBE6]"}`}
                >
                  🟢 نقد
                </button>
              </div>
              {isCashSale ? (
                <div className="bg-[#DCFCE7] border border-[#15803D] rounded-lg p-2 text-[11px] text-[#14532D] leading-snug">
                  <b>بيع نقدي</b> — المبلغ كامل يُضاف للصندوق الرئيسي ولا ينضاف لدين الزبون.
                </div>
              ) : (
                <div className="bg-[#FEE2E2] border border-[#DC2626] rounded-lg p-2 text-[11px] text-[#7F1D1D] leading-snug">
                  هذه الفاتورة <b>دين كامل</b> على الزبون. الخصم يتم لاحقاً فقط عبر <b>سند قبض</b>.
                </div>
              )}
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onKeyDown={commitOnEnter}
                data-testid="ds-notes"
                placeholder="ملاحظة · Enter لتثبيت"
                className="w-full px-3 py-1.5 rounded-xl border border-[#E4EBE6] text-xs"
              />

              {/* Customer-account accrual toggle (rewards + monthly contest)
                  — defaults ON. Admin can switch OFF for an unusual cash-only
                  walk-in that shouldn't credit rewards or count in the contest. */}
              <label className="flex items-start gap-2 bg-[#FFFBEB] border border-[#F5A623]/40 rounded-xl p-2.5 text-[11px] text-[#7c5510] leading-snug cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={accrueToCustomer}
                  onChange={(e) => setAccrueToCustomer(e.target.checked)}
                  data-testid="ds-accrue-toggle"
                  className="mt-0.5 accent-[#15803D]"
                />
                <span>
                  <b>إضافة لحساب الزبون</b> — تُحسب القطع للمسابقة الشهرية
                  وتضاف ١٥٠ د.ع/قطعة لمكافآته.
                  <br />
                  {accrueToCustomer ? (
                    <span className="text-[#14532D]">✅ مفعّل — البيع يُحتسب للزبون.</span>
                  ) : (
                    <span className="text-[#7F1D1D]">⛔ موقوف — بيع نقدي بدون مكافآت أو مسابقة.</span>
                  )}
                </span>
              </label>
            </div>
            <button
              onClick={openReceipt}
              disabled={cart.length === 0}
              data-testid="ds-checkout-btn"
              className="mt-3 w-full bg-[#7ED321] hover:bg-[#6cb91d] disabled:bg-[#BAE6FD] disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-full flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              تم الشراء — معاينة الفاتورة
            </button>
          </div>
        </div>
      </div>

      {showReceipt && (
        <ReceiptPreview
          payload={{
            customer_name: customer.customer_name,
            customer_phone: customer.customer_phone,
            items: cart,
            total: subtotal,
            amount_paid: 0,
          }}
          onClose={() => setShowReceipt(false)}
          onConfirm={confirmSale}
          saving={saving}
          mujahezList={mujahezList}
          assignedMujahezId={assignedMujahezId}
          onAssignChange={setAssignedMujahezId}
        />
      )}

      {postSaleOrder && (
        <PostSaleAssignment
          order={postSaleOrder}
          onPrint={() => { setSavedOrder({ ...postSaleOrder }); setPostSaleOrder(null); }}
          onSkip={() => { setSavedOrder({ ...postSaleOrder }); setPostSaleOrder(null); }}
        />
      )}

      {savedOrder && (
        <InvoiceModal
          order={savedOrder}
          customerInfo={{
            // InvoiceModal computes previousBalance = customer_debt - amount_remaining.
            // Passing (old debt + this invoice's unpaid portion) makes it correctly
            // display the customer's OLD debt as "الدين المتبقي السابق" on the print.
            customer_debt:
              Number(savedOrder.previous_customer_debt || 0) +
              Number(savedOrder.amount_remaining || 0),
          }}
          onClose={() => setSavedOrder(null)}
        />
      )}
    </div>
  );
};

export default DirectSaleTab;
